﻿import * as React from 'react';
import {
    AddedProduct,
    ChangeStatus,
    ExchangeableProduct,
    OrderRowChangeStatus,
    ParseTranslations
} from '../EditPage';
import ChangeAddedProductButton from './ChangeAddedProductButton';
import { Button } from 'react-bootstrap';
import ConfirmationModal from './ConfirmationModal';
import { TranslatedLabels } from '../../../../../shared/types';

type AddedProductsProps = {
    products: AddedProduct[];
    labels: TranslatedLabels;
    parseTranslations: ParseTranslations;
    onRemoveExtraProduct: (id: string) => void;
    onChangeAddedProductFactory: (orderRowId: number) => (product: ExchangeableProduct) => void;
    showRemoveExtraProductConfirmationModal: boolean;
    onConfirmRemoveExtraProductConfirmationModal: () => void;
    onCancelRemoveExtraProductConfirmationModal: () => void;
    onCloseRemoveExtraProductConfirmationModal: () => void;
};

export type IExchangeableProductButton = {
    id: string;
    productId: number;
    name: string;
    disabled: boolean;
    isAnExtraProduct: boolean;
};

function shouldRenderRemoveButton(
    isRemovable: boolean,
    changeStatus: ChangeStatus | null
): boolean {
    if (!changeStatus) {
        return isRemovable;
    }

    return isRemovable && changeStatus.status !== OrderRowChangeStatus.RemovedByPhotographer;
}

function shouldRenderEditButton(product: AddedProduct): boolean {
    if (!product.changeStatus) {
        return product.isExchangeable;
    }

    return (
        product.isExchangeable &&
        product.changeStatus.status !== OrderRowChangeStatus.RemovedByPhotographer
    );
}

function removedProduct(product: AddedProduct): boolean {
    if (
        product.changeStatus &&
        product.changeStatus.status === OrderRowChangeStatus.RemovedByPhotographer
    ) {
        return false;
    }
    return true;
}

function transformExchangeableProductToExchangeableProductButtonFactory(
    currentProduct: AddedProduct,
    products: AddedProduct[]
) {
    return function transformExchangeableProductToExchangeableProductButton(
        exchangeableProduct: ExchangeableProduct
    ): IExchangeableProductButton {
        return {
            ...exchangeableProduct,
            disabled:
                exchangeableProduct.productId === currentProduct.productId ||
                products
                    .filter(removedProduct)
                    .find((product) => product.productId === exchangeableProduct.productId) !==
                    undefined
        };
    };
}

function AddedProducts({
    products,
    labels,
    parseTranslations,
    onChangeAddedProductFactory,
    onRemoveExtraProduct,
    showRemoveExtraProductConfirmationModal,
    onConfirmRemoveExtraProductConfirmationModal,
    onCancelRemoveExtraProductConfirmationModal,
    onCloseRemoveExtraProductConfirmationModal
}: AddedProductsProps) {
    return (
        <div>
            <h3 className="addedProducts__title">{labels.productColumnTitle}</h3>
            <div className="addedProducts__titleContentDivider" />
            {products
                .filter((product) => !product.removed)
                .map((product) => {

                const isOrderRowDeleted = 
                    product.changeStatus &&
                    product.changeStatus.status === OrderRowChangeStatus.RemovedByPhotographer;

                const exchangeableProducts = product.exchangeableProducts
                    ? product.exchangeableProducts.map(
                          transformExchangeableProductToExchangeableProductButtonFactory(
                              product,
                              products
                          )
                      )
                    : ([] as IExchangeableProductButton[]);
                return (
                    <div
                        className={`addedProducts__productContainer${
                            isOrderRowDeleted ? '--deleted' : ''
                        }`}
                        key={product.id}
                    >
                        <span className="addedProducts__productName">{product.name}</span>
                        {product.changeStatus ? (
                            <span className="addedProducts__changeStatus">
                                {product.changeStatus.text}
                            </span>
                        ) : null}
                        <div className="addedProducts__productActionContainer">
                            {shouldRenderEditButton(product) ? (
                                <ChangeAddedProductButton
                                    exchangeableProducts={exchangeableProducts}
                                    labels={labels}
                                    parseTranslations={parseTranslations}
                                    onChange={onChangeAddedProductFactory(product.orderRowId)}
                                />
                            ) : null}
                            {shouldRenderRemoveButton(product.isRemovable, product.changeStatus) ? (
                                <button
                                    className="addedProducts__removeOrderRowButton"
                                    onClick={() => {
                                        onRemoveExtraProduct(product.id);
                                    }}
                                >
                                    <i className="material-icons addedProducts__removeOrderRowButtonIcon">
                                        delete
                                    </i>
                                    <span>{labels.removeButtonLabel}</span>
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            <ConfirmationModal
                onClose={onCloseRemoveExtraProductConfirmationModal}
                isOpen={showRemoveExtraProductConfirmationModal}
                title={labels.removeOrderRow__confirmationModal__title}
                textContent={<p>{labels.removeOrderRow__confirmationModal__text}</p>}
            >
                <Button
                    className="addAdditionalProduct__removeOrderRowConfirmButton"
                    variant="primary"
                    onClick={onConfirmRemoveExtraProductConfirmationModal}
                >
                    {labels.removeOrderRow__confirmationModal__confirmButtonLabel}
                </Button>
                <Button variant="secondary" onClick={onCancelRemoveExtraProductConfirmationModal}>
                    {labels.removeOrderRow__confirmationModal__cancelButtonLabel}
                </Button>
            </ConfirmationModal>
        </div>
    );
}

export default AddedProducts;
